import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AESUtil } from '../../util/aes-util';
import { CryptoMode, CryptoPolicy, resolveCryptoPolicy } from './crypto-policy';

/** JSON body if it parses, otherwise the original text. */
function parseJsonOrRaw(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/**
 * Applies payload encryption to every backend call from one place.
 *
 * The 451 generated `fn/*` helpers, the hand-written services and the components all
 * share a single `HttpClient` and a single `ApiConfiguration.rootUrl`, so this is the
 * only hop every request is guaranteed to pass through — which is why none of them
 * need to know encryption exists.
 *
 * Must be registered AFTER `JwtInterceptor` so that on the way back it unwraps the
 * body first and `JwtInterceptor`'s error handling sees plaintext (response
 * interceptors run in reverse registration order).
 */
@Injectable()
export class CryptoInterceptor implements HttpInterceptor {
  private get mode(): CryptoMode {
    return (environment as { encryption?: { mode?: CryptoMode } }).encryption?.mode ?? 'legacy';
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const policy = resolveCryptoPolicy(request, environment.apiUrl, this.mode);
    if (!policy.encryptRequest && !policy.decryptResponse) {
      return next.handle(request);
    }

    return next.handle(this.applyRequestPolicy(request, policy)).pipe(
      map((event) =>
        policy.decryptResponse && event instanceof HttpResponse
          ? this.decryptResponse(event, request.url)
          : event
      ),
      catchError((error: HttpErrorResponse) =>
        throwError(() =>
          policy.decryptResponse ? this.decryptError(error) : error
        )
      )
    );
  }

  private applyRequestPolicy(
    request: HttpRequest<unknown>,
    policy: CryptoPolicy
  ): HttpRequest<unknown> {
    let outbound = request;
    if (policy.encryptRequest) {
      outbound = outbound.clone({ body: { encryptedData: AESUtil.encrypt(request.body) } });
    }
    if (policy.decryptResponse) {
      // Angular parses the ciphertext as JSON and throws unless we take it as text.
      outbound = outbound.clone({ responseType: 'text' });
    }
    return outbound;
  }

  private decryptResponse(response: HttpResponse<unknown>, url: string): HttpResponse<unknown> {
    const body = String(response.body ?? '').trim();
    if (!body) {
      return response.clone({ body: null });
    }
    try {
      const plain = AESUtil.decrypt(body);
      if (plain !== null && plain !== undefined) {
        return response.clone({ body: plain });
      }
    } catch (e) {
      console.warn('[crypto] could not decrypt response for', url, e);
    }
    // Not ciphertext we can read. When an endpoint is still served in plaintext —
    // backend rollout in progress, or `payload-encryption.enabled` off — hand callers
    // the parsed JSON rather than the raw string: components run `.forEach` /
    // `.filter` over these results and a string breaks them in a way that looks like
    // a data bug rather than a transport one.
    return response.clone({ body: parseJsonOrRaw(body) });
  }

  /**
   * Best-effort decryption of an error body.
   *
   * Whether `PayloadEncryptionAdvice` also covers `@ExceptionHandler` responses is
   * still unconfirmed, so this has to work either way: a body that isn't ciphertext
   * is handed back untouched rather than replaced with garbage. Downstream error
   * handling — the SBL toast and reference-id extraction in `JwtInterceptor` — then
   * reads plaintext in both cases.
   */
  private decryptError(error: HttpErrorResponse): HttpErrorResponse {
    const raw = error.error;
    if (typeof raw !== 'string') {
      return error;
    }
    const cipherText = raw.trim();
    if (!cipherText) {
      return error;
    }
    let plain: unknown;
    try {
      plain = AESUtil.decrypt(cipherText);
    } catch {
      return error;
    }
    // `AESUtil.decrypt` echoes its input back when the text wasn't ciphertext.
    if (plain === null || plain === undefined || plain === cipherText) {
      return error;
    }
    return new HttpErrorResponse({
      error: plain,
      headers: error.headers,
      status: error.status,
      statusText: error.statusText,
      url: error.url ?? undefined,
    });
  }
}
